import { AnimatePresence, motion } from "motion/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { UserConversationsInterface, UsersModel } from "../../../../Models/Users/Users.Model";
import { ClosedUserCard } from "../Components/Conversations.ClosedUserCard";
import { useInView } from "react-intersection-observer";
import { CgSpinner } from "react-icons/cg";
import { UserInterface } from "@swivl-mono/types";

interface ConversationsClosedUserListProps {
  conversations?: { [userId: number]: UserConversationsInterface };
  searchTerm?: string;
  selectedUserId?: number;
  allowedLocationsMap?: { [locationId: string]: boolean }; // Map of location IDs to enabled status
}

export function ConversationsClosedUserList(props: ConversationsClosedUserListProps) {
  const { searchTerm, selectedUserId, conversations, allowedLocationsMap } = props;
  const { loadedUsersPage, isLoadingMoreUsers, moreUsersToLoad, users, usersOnline } = UsersModel.useState((s) => s);
  const [foundUsers, setFoundUsers] = useState<UserInterface[]>([]);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { ref, inView } = useInView();

  const filteredUsers = useMemo(() => {
    if (!users) { return []; }
    if (!allowedLocationsMap) { return [] }

    return Object.values(users).filter(user => {
      if (user.locationId && !allowedLocationsMap[user.locationId]) {
        return false;
      }

      if (conversations && conversations[user.id]) {
        return false;
      }

      if ((user as any)?.deletedDate) {
        return false;
      }

      return true;
    }).sort((a, b) => {
      const aDate = a.lastUserMessageAt || a.lastInteractionAt || a.updatedAt || a.createdAt;
      const bDate = b.lastUserMessageAt || b.lastInteractionAt || b.updatedAt || b.createdAt;
      if (aDate && bDate) {
        return bDate.getTime() - aDate.getTime(); // Sort by last interaction date
      }
      if (aDate) return -1; // a has a date, b does not
      if (bDate) return 1; // b has a date, a does not    
      return 0; // Both have no date, keep original order
    });
  }, [users, conversations, allowedLocationsMap, usersOnline]);

  // search for closed users
  useEffect(() => {
    if (searchTerm) {
      const searchForUser = async () => {
        const foundUsers = await UsersModel.searchForUser(searchTerm);
        setFoundUsers(foundUsers ? foundUsers : []);
      };

      searchForUser();
    }
    else {
      setFoundUsers([]);
    }
  }, [searchTerm]);

  useEffect(() => {
    UsersModel.loadUsers();
  }, []);

  useEffect(() => {
    if (!inView || !users || !moreUsersToLoad) return;

    if (containerRef.current && containerRef.current.scrollHeight <= containerRef.current.clientHeight) return; // no load if no scroll

    // throttle loading users
    const timeout = setTimeout(() => {
      UsersModel.loadUsers(loadedUsersPage + 1);
    }, 300);

    return () => clearTimeout(timeout);
  }, [inView, users, loadedUsersPage]);

  const fadeVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 }
  }

  const cardVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20, scale: 0.95 }
  }

  const usersToRender = searchTerm ? foundUsers : filteredUsers?.length > 0 ? filteredUsers : [];

  return (
    <motion.div
      className="py-6 px-5 h-full flex flex-col overflow-y-auto [&::-webkit-scrollbar]:bg-transparent [&::-webkit-scrollbar]:rounded-md [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:bg-grey-400 [&::-webkit-scrollbar-thumb]:rounded-md"
      key="closed"
      initial="hidden"
      animate="visible"
      exit="exit"
      ref={containerRef}
      variants={fadeVariants}
      transition={{ duration: 0.2, ease: "easeInOut" }}
    >
      <AnimatePresence mode="popLayout">
        {usersToRender.length > 0 ? usersToRender.map((user) => (
          <motion.div
            key={user.id}
            initial={false}
            layout
            animate="visible"
            exit="exit"
            variants={cardVariants}
            transition={{ duration: 0.2, ease: "easeInOut" }}
          >
            <ClosedUserCard
              user={user}
              selectedUserId={selectedUserId}
              isOnline={user?.id ? usersOnline?.[user?.id]?.online : false}
            />
          </motion.div>
        )) : (
          <div className="p-6 text-center bg-grey-200 border rounded-lg">
            <p className="text-grey-600">No closed conversations</p>
          </div>
        )}
      </AnimatePresence>

      {/* triggers intersection-observer inview */}
      <div className="h-px" ref={ref} />
      {isLoadingMoreUsers && moreUsersToLoad && <div className="flex justify-center"><CgSpinner className="animate-spin mt-4" /></div>}
    </motion.div>
  )
}
